/**
 * Client-side mirror of the backend system-prompt budget (CHG-AGT-008).
 *
 * The authoritative ceiling lives in `TokenBudgetPlanner`
 * (`api/src/llm_config/token_budget.py`, ADR-027). This helper only powers the
 * soft counter/warning in the editor: the backend always decides. Keep the
 * context windows in sync with `api/src/llm_config/token_counter.py`.
 */

export const DEFAULT_CONTEXT_WINDOW = 128_000;
export const DEFAULT_MAX_OUTPUT_TOKENS = 4096;
export const MIN_SYSTEM_PROMPT = 2000;
export const MIN_HISTORY = 1000;
export const MIN_USER_MESSAGE = 500;
export const SYSTEM_PROMPT_RATIO = 0.5;
export const HISTORY_RATIO = 0.4;
export const USER_MESSAGE_RATIO = 0.1;
export const SAFETY_MARGIN_RATIO = 0.05;
const LEGACY_SYSTEM_PROMPT_BUDGET = 4000;

/** Mirrors `CONTEXT_WINDOWS` in `api/src/llm_config/token_counter.py`. */
const CONTEXT_WINDOWS: Record<string, number> = {
  'claude-opus-4-5': 200_000,
  'claude-haiku-4-5': 200_000,
  'claude-sonnet-4-5': 200_000,
  'claude-3-5-sonnet': 200_000,
  'claude-3-5-haiku': 200_000,
  'claude-3-opus': 200_000,
  'claude-3-sonnet': 200_000,
  'claude-3-haiku': 200_000,
  'gpt-5.2': 128_000,
  'gpt-5.2-chat': 128_000,
  'gpt-4o': 128_000,
  'gpt-4o-mini': 128_000,
  'gpt-4-turbo': 128_000,
  'gpt-4': 8_192,
  'gpt-3.5-turbo': 16_385,
  'text-embedding-3-large': 8_192,
  'text-embedding-3-small': 8_192,
  'text-embedding-ada-002': 8_192,
  'llama3.1': 128_000,
  'llama3.2': 128_000,
  llama3: 8_192,
};

export interface ModelLike {
  model?: string | null;
  catalog_slug?: string | null;
  max_tokens?: number | null;
}

export interface PromptBudget {
  tokens: number;
  budget: number;
  model: string;
  overBudget: boolean;
}

/** Model-agnostic approximation (~4 chars/token), same idea as ADR-027. */
export function estimatePromptTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

function resolveContextWindow(model: string): number {
  if (!model) return DEFAULT_CONTEXT_WINDOW;
  if (CONTEXT_WINDOWS[model]) return CONTEXT_WINDOWS[model];

  const lower = model.toLowerCase();
  for (const [key, value] of Object.entries(CONTEXT_WINDOWS)) {
    const keyLower = key.toLowerCase();
    if (keyLower.includes(lower) || lower.includes(keyLower)) return value;
  }
  return DEFAULT_CONTEXT_WINDOW;
}

/** Mirrors `TokenBudgetPlanner.plan()` → `system_prompt_budget`. */
export function systemPromptBudget(mainModel?: ModelLike | null): number {
  const model = mainModel?.model || mainModel?.catalog_slug || '';
  const contextWindow = resolveContextWindow(model);
  const maxOutput =
    typeof mainModel?.max_tokens === 'number' && mainModel.max_tokens > 0
      ? mainModel.max_tokens
      : DEFAULT_MAX_OUTPUT_TOKENS;
  const safetyMargin = Math.floor(contextWindow * SAFETY_MARGIN_RATIO);
  const available = contextWindow - maxOutput - safetyMargin;

  if (available <= 0) return LEGACY_SYSTEM_PROMPT_BUDGET;

  let systemPrompt = Math.max(MIN_SYSTEM_PROMPT, Math.floor(available * SYSTEM_PROMPT_RATIO));
  const history = Math.max(MIN_HISTORY, Math.floor(available * HISTORY_RATIO));
  const userMessage = Math.max(
    MIN_USER_MESSAGE,
    Math.floor(available * USER_MESSAGE_RATIO)
  );

  const totalAllocated = systemPrompt + history + userMessage;
  if (totalAllocated > available) {
    const scale = available / totalAllocated;
    systemPrompt = Math.max(MIN_SYSTEM_PROMPT, Math.floor(systemPrompt * scale));
  }

  return systemPrompt;
}

export function computePromptBudget(
  systemPrompt: string,
  mainModel?: ModelLike | null
): PromptBudget {
  const tokens = estimatePromptTokens(systemPrompt);
  const budget = systemPromptBudget(mainModel);
  const model = mainModel?.model || mainModel?.catalog_slug || '';

  return { tokens, budget, model, overBudget: tokens > budget };
}
